import { predefinedPatterns } from './patterns';
import { EnvVarSpec } from './schema';

// Schema is an object where every key maps to a EnvVarSpec<T>
export type EnvSchema = Record<string, EnvVarSpec<unknown>>;

export type InferValue<T> = T extends EnvVarSpec<infer V> ? V : never;

export type EnvResult<T extends EnvSchema> = {
  [K in keyof T]: InferValue<T[K]>;
};

type StrictRuntimeEnv<T extends EnvSchema> = {
  [K in keyof T]: string | undefined;
};

export function createEnv<T extends EnvSchema>(
  schema: T,
  runtimeEnv: StrictRuntimeEnv<T>
): EnvResult<T> & {
  validate(env?: StrictRuntimeEnv<T>): void;
  outputDebug(label?: string, env?: StrictRuntimeEnv<T>): void;
} {
  const internalEnv = resolve(schema, runtimeEnv);

  function validate(env: typeof runtimeEnv = runtimeEnv) {
    const missing: string[] = [];
    const invalid: string[] = [];

    for (const key of Object.keys(schema) as (keyof T)[]) {
      const item = schema[key]!;
      const keyStr = String(key);
      const raw = env[key];
      const value = raw ?? item.default;

      const isMissing = value === undefined || value === '';

      if (item.required && item.default === undefined && isMissing) {
        missing.push(keyStr);
        continue;
      }

      // Skip further validation if optional and missing
      if (isMissing) continue;

      let coerced: unknown = value;

      // Coercion & type validation
      switch (item.type) {
        case 'number': {
          const num = typeof value === 'number' ? value : Number(value);
          if (isNaN(num)) {
            invalid.push(`${keyStr} = ${JSON.stringify(value)} (expected a number)`);
            continue;
          }
          coerced = num;
          break;
        }
        case 'boolean': {
          const str = String(value).toLowerCase();
          if (!['true', 'false', '1', '0'].includes(str)) {
            invalid.push(`${keyStr} = ${JSON.stringify(value)} (expected true, false, 1 or 0)`);
            continue;
          }
          coerced = str === 'true' || str === '1';
          break;
        }
        case 'string': {
          if (typeof value !== 'string') {
            invalid.push(`${keyStr} = ${JSON.stringify(value)} (expected a string)`);
            continue;
          }
          coerced = value.trim();
          break;
        }
      }

      // Match regex
      if (typeof coerced === 'string' && item.match) {
        const pattern =
          typeof item.match === 'string' ? predefinedPatterns[item.match] : item.match;
        if (!(pattern instanceof RegExp)) {
          invalid.push(`${keyStr} = ${JSON.stringify(value)} (invalid pattern: ${item.match})`);
        } else if (!pattern.test(coerced)) {
          invalid.push(`${keyStr} = ${JSON.stringify(value)} (does not match pattern ${pattern})`);
        }
      }

      // Length checks for strings
      if (typeof coerced === 'string') {
        if (item.minLength !== undefined && coerced.length < item.minLength) {
          invalid.push(
            `${keyStr} = ${JSON.stringify(value)} (minimum length is ${item.minLength})`
          );
        }
        if (item.maxLength !== undefined && coerced.length > item.maxLength) {
          invalid.push(
            `${keyStr} = ${JSON.stringify(value)} (maximum length is ${item.maxLength})`
          );
        }
      }

      // Validate choices
      if (item.choices && !item.choices.includes(coerced as (typeof item.choices)[number])) {
        invalid.push(
          `${keyStr} = ${JSON.stringify(value)} (expected one of: ${item.choices.map((c) => JSON.stringify(c)).join(', ')})`
        );
      }
    }

    if (missing.length > 0 || invalid.length > 0) {
      const lines: string[] = ['Environment validation failed:'];
      if (missing.length > 0) {
        lines.push('Missing required environment variables:', ...missing.map((k) => `- ${k}`));
      }
      if (invalid.length > 0) {
        lines.push(
          'Invalid values for environment variables:',
          ...invalid.map((msg) => `- ${msg}`)
        );
      }
      throw new Error(lines.join('\n'));
    }
  }

  function outputDebug(label: string = '', env: typeof runtimeEnv = runtimeEnv) {
    const output: Record<string, string | number | boolean> = {};

    function formatMasked(value?: string): string {
      if (!value) return '';
      if (value.length === 1) return value;
      if (value.length < 4) return value[0] + '*'.repeat(value.length - 1);
      return value.slice(0, 2) + '***' + value.slice(-1);
    }

    function formatValue(key: keyof T, value: unknown): string | number | boolean {
      const item = schema[key as keyof T];
      if (!item) return value as string | number | boolean;

      if (item.mask && typeof value === 'string') {
        return formatMasked(value);
      }

      if (typeof value === 'string') {
        switch (item.type) {
          case 'boolean':
            return value === 'true' || value === '1';
          case 'number':
            return Number(value);
          default:
            return value;
        }
      }

      return value as string | number | boolean;
    }

    for (const key of Object.keys(schema) as (keyof T)[]) {
      const item = schema[key]!;
      const raw = env[key];
      const fallbackUsed = raw === undefined;
      const finalValue = fallbackUsed ? item.default : raw;
      output[key as string] = formatValue(key as string, finalValue);
    }

    console.log(`${JSON.stringify({ source: label, vars: output })}`);
  }

  return Object.assign(internalEnv, { validate, outputDebug });
}

function resolve<T extends EnvSchema>(schema: T, env: StrictRuntimeEnv<T>): EnvResult<T> {
  const rawResult = {} as Partial<EnvResult<T>>;

  for (const key of Object.keys(schema) as (keyof T)[]) {
    const item = schema[key]!;
    const raw = env[key];

    if (raw !== undefined) {
      switch (item.type) {
        case 'string':
          rawResult[key] = raw.trim() as InferValue<typeof item>;
          break;

        case 'boolean': {
          const str = raw.toLowerCase();
          const val = str === 'true' || str === '1';
          rawResult[key] = val as InferValue<typeof item>;
          break;
        }

        case 'number': {
          const num = Number(raw);
          rawResult[key] = isNaN(num)
            ? (raw as InferValue<typeof item>)
            : (num as InferValue<typeof item>);
          break;
        }
      }
    } else {
      rawResult[key] = item.default as InferValue<typeof item>;
    }
  }

  return rawResult as EnvResult<T>;
}
